// Real-time tracking service using polling

import { apiService } from './api';
import type { Order, Shipment, Route, WebhookEvent } from '@/types';

export interface RealtimeUpdate {
  type: 'order' | 'shipment' | 'route' | 'webhook' | 'notification';
  action: 'created' | 'updated' | 'deleted' | 'status_changed';
  data: any;
  timestamp: string;
}

export interface NotificationMessage {
  id: string;
  type: 'success' | 'warning' | 'error' | 'info';
  title: string;
  message: string;
  timestamp: string;
  read: boolean;
  actionUrl?: string;
}

type SubscriptionCallback = (update: RealtimeUpdate) => void;
type NotificationCallback = (notification: NotificationMessage) => void;

class RealtimeService {
  private subscriptions: Map<string, SubscriptionCallback[]> = new Map();
  private notificationCallbacks: NotificationCallback[] = [];
  private intervalIds: Map<string, number> = new Map();
  private lastPolled: Map<string, string> = new Map();
  private isPolling = false;
  private notifications: NotificationMessage[] = [];

  // Default polling intervals (in milliseconds)
  private readonly intervals = {
    orders: 30000,     // 30 seconds
    shipments: 45000,  // 45 seconds  
    routes: 60000,     // 1 minute
    webhooks: 120000,  // 2 minutes
    dashboard: 60000,  // 1 minute for dashboard stats
  };

  /**
   * Subscribe to real-time updates for a specific entity type
   */
  subscribe(entityType: string, callback: SubscriptionCallback): () => void {
    if (!this.subscriptions.has(entityType)) {
      this.subscriptions.set(entityType, []);
    }
    
    this.subscriptions.get(entityType)!.push(callback);
    
    // Start polling for this entity type if not already started
    this.startPolling(entityType);

    // Return unsubscribe function
    return () => {
      const callbacks = this.subscriptions.get(entityType);
      if (callbacks) {
        const index = callbacks.indexOf(callback);
        if (index > -1) {
          callbacks.splice(index, 1);
        }
        
        // Stop polling if no more subscribers
        if (callbacks.length === 0) {
          this.stopPolling(entityType);
        }
      }
    };
  }

  /**
   * Subscribe to notifications
   */
  subscribeToNotifications(callback: NotificationCallback): () => void {
    this.notificationCallbacks.push(callback);
    
    // Send existing unread notifications
    this.notifications
      .filter(n => !n.read)
      .forEach(notification => callback(notification));
    
    return () => {
      const index = this.notificationCallbacks.indexOf(callback);
      if (index > -1) {
        this.notificationCallbacks.splice(index, 1);
      }
    };
  }

  /**
   * Start polling for a specific entity type
   */
  private startPolling(entityType: string): void {
    if (this.intervalIds.has(entityType)) {
      return; // Already polling
    }

    const interval = this.intervals[entityType as keyof typeof this.intervals] || 60000;
    
    const intervalId = window.setInterval(() => {
      this.pollForUpdates(entityType);
    }, interval);

    this.intervalIds.set(entityType, intervalId);
    
    // Poll immediately
    this.pollForUpdates(entityType);
  }

  /**
   * Stop polling for a specific entity type
   */
  private stopPolling(entityType: string): void {
    const intervalId = this.intervalIds.get(entityType);
    if (intervalId) {
      clearInterval(intervalId);
      this.intervalIds.delete(entityType);
    }
  }

  /**
   * Poll for updates from the API
   */
  private async pollForUpdates(entityType: string): Promise<void> {
    try {
      const lastPolledTime = this.lastPolled.get(entityType);
      const currentTime = new Date().toISOString();
      
      let updates: RealtimeUpdate[] = [];

      switch (entityType) {
        case 'orders':
          updates = await this.pollOrders(lastPolledTime);
          break;
        case 'shipments':
          updates = await this.pollShipments(lastPolledTime);
          break;
        case 'routes':
          updates = await this.pollRoutes(lastPolledTime);
          break;
        case 'webhooks':
          updates = await this.pollWebhooks(lastPolledTime);
          break;
        case 'dashboard':
          updates = await this.pollDashboardUpdates();
          break;
      }

      // Update last polled time
      this.lastPolled.set(entityType, currentTime);

      // Notify subscribers
      if (updates.length > 0) {
        const callbacks = this.subscriptions.get(entityType) || [];
        updates.forEach(update => {
          callbacks.forEach(callback => callback(update));
        });

        // Generate notifications for important updates
        this.generateNotifications(updates);
      }

    } catch (error) {
      console.error(`Error polling ${entityType}:`, error);
    }
  }

  /**
   * Poll for order updates
   */
  private async pollOrders(since?: string): Promise<RealtimeUpdate[]> {
    const filters: any = { per_page: 50 };
    if (since) {
      filters.updated_since = since;
    }

    const response = await apiService.getOrders(filters);
    const orders = response.data || [];

    return orders.map(order => ({
      type: 'order' as const,
      action: 'updated' as const,
      data: order,
      timestamp: order.updated_at,
    }));
  }

  /**
   * Poll for shipment updates
   */
  private async pollShipments(since?: string): Promise<RealtimeUpdate[]> {
    const filters: any = { per_page: 50 };
    if (since) {
      filters.updated_since = since;
    }

    const response = await apiService.getShipments(filters);
    const shipments = response.data || [];

    return shipments.map(shipment => ({
      type: 'shipment' as const,
      action: 'updated' as const,
      data: shipment,
      timestamp: shipment.created_at, // Using created_at as proxy for updated_at
    }));
  }

  /**
   * Poll for route updates
   */
  private async pollRoutes(since?: string): Promise<RealtimeUpdate[]> {
    const today = new Date().toISOString().split('T')[0];
    const response = await apiService.getRoutes(today);
    const routes = response.routes || [];

    return routes.map(route => ({
      type: 'route' as const,
      action: 'updated' as const,
      data: route,
      timestamp: new Date().toISOString(),
    }));
  }

  /**
   * Poll for webhook updates
   */
  private async pollWebhooks(since?: string): Promise<RealtimeUpdate[]> {
    const filters: any = { per_page: 20 };
    if (since) {
      filters.created_since = since;
    }

    const response = await apiService.getWebhookEvents(filters);
    const events = response.data || [];

    return events.map(event => ({
      type: 'webhook' as const,
      action: 'created' as const,
      data: event,
      timestamp: event.created_at,
    }));
  }

  /**
   * Poll for dashboard updates
   */
  private async pollDashboardUpdates(): Promise<RealtimeUpdate[]> {
    const stats = await apiService.getDashboardStats();
    
    return [{
      type: 'notification' as const,
      action: 'updated' as const,
      data: stats,
      timestamp: new Date().toISOString(),
    }];
  }

  /**
   * Generate notifications based on updates
   */
  private generateNotifications(updates: RealtimeUpdate[]): void {
    const notifications: NotificationMessage[] = [];

    updates.forEach(update => {
      switch (update.type) {
        case 'order':
          if (update.data.status === 'shipped') {
            notifications.push({
              id: `order-shipped-${update.data.id}`,
              type: 'success',
              title: 'Order Shipped',
              message: `Order ${update.data.order_number} has been shipped`,
              timestamp: update.timestamp,
              read: false,
              actionUrl: `/orders/${update.data.id}`,
            });
          } else if (update.data.status === 'cancelled') {
            notifications.push({
              id: `order-cancelled-${update.data.id}`,
              type: 'warning',
              title: 'Order Cancelled',
              message: `Order ${update.data.order_number} was cancelled`,
              timestamp: update.timestamp,
              read: false,
              actionUrl: `/orders/${update.data.id}`,
            });
          }
          break;

        case 'shipment':
          if (update.data.status === 'delivered') {
            notifications.push({
              id: `shipment-delivered-${update.data.id}`,
              type: 'success',
              title: 'Delivery Completed',
              message: `Shipment ${update.data.tracking_number} has been delivered`,
              timestamp: update.timestamp,
              read: false,
              actionUrl: `/shipments/${update.data.id}`,
            });
          } else if (update.data.status === 'failed_delivery') {
            notifications.push({
              id: `shipment-failed-${update.data.id}`,
              type: 'error',
              title: 'Delivery Failed',
              message: `Shipment ${update.data.tracking_number} delivery failed`,
              timestamp: update.timestamp,
              read: false,
              actionUrl: `/shipments/${update.data.id}`,
            });
          }
          break;

        case 'webhook':
          if (update.data.status === 'failed') {
            notifications.push({
              id: `webhook-failed-${update.data.id}`,
              type: 'error',
              title: 'Webhook Failed',
              message: `${update.data.source} webhook failed: ${update.data.event_type}`,
              timestamp: update.timestamp,
              read: false,
              actionUrl: `/webhooks`,
            });
          }
          break;
      }
    });

    // Add new notifications
    notifications.forEach(notification => {
      // Avoid duplicates
      if (!this.notifications.some(n => n.id === notification.id)) {
        this.notifications.unshift(notification);
        
        // Limit to last 100 notifications
        if (this.notifications.length > 100) {
          this.notifications = this.notifications.slice(0, 100);
        }

        // Notify subscribers
        this.notificationCallbacks.forEach(callback => callback(notification));
      }
    });
  }

  /**
   * Mark notification as read
   */
  markNotificationAsRead(notificationId: string): void {
    const notification = this.notifications.find(n => n.id === notificationId);
    if (notification) {
      notification.read = true;
    }
  }

  /**
   * Get all notifications
   */
  getNotifications(): NotificationMessage[] {
    return [...this.notifications];
  }

  /**
   * Get unread notification count
   */
  getUnreadCount(): number {
    return this.notifications.filter(n => !n.read).length;
  }

  /**
   * Force refresh for a specific entity type
   */
  refresh(entityType: string): void {
    this.lastPolled.delete(entityType);
    this.pollForUpdates(entityType);
  }

  /**
   * Stop all polling
   */
  stopAll(): void {
    this.intervalIds.forEach((intervalId) => {
      clearInterval(intervalId);
    });
    this.intervalIds.clear();
    this.subscriptions.clear();
    this.notificationCallbacks.length = 0;
  }

  /**
   * Change polling interval for an entity type
   */
  setPollingInterval(entityType: string, intervalMs: number): void {
    this.intervals[entityType as keyof typeof this.intervals] = intervalMs;
    
    // Restart polling with new interval if currently active
    if (this.intervalIds.has(entityType)) {
      this.stopPolling(entityType);
      this.startPolling(entityType);
    }
  }

  /**
   * Get current polling status
   */
  getPollingStatus(): Record<string, boolean> {
    const status: Record<string, boolean> = {};
    Object.keys(this.intervals).forEach(entityType => {
      status[entityType] = this.intervalIds.has(entityType);
    });
    return status;
  }
}

export const realtimeService = new RealtimeService();
export default realtimeService;